import { useEffect, useRef } from "react";
import { useStytch, useStytchUser } from "../utils/useStytch";
import {
  loadOnboardingData,
  clearOnboardingData,
} from "../onboarding/onboardingStorage";
import {
  hasSaveableData,
  useSaveOnboardingToApi,
} from "./useSaveOnboardingData";
import {
  createOnboardingSaveKey,
  createOnboardingSaveIdempotencyKey,
  runOnboardingSaveOnce,
} from "./onboardingSaveGuard";

const METADATA_MAX_RETRIES = 15;
const METADATA_RETRY_DELAY_MS = 500;

/**
 * App-level rescue for locally stored onboarding answers (#1393).
 *
 * The onboarding save used to run only from CreateAccountScreen. Email/OTP
 * and web OAuth flows pass back through that screen after auth, but native
 * OAuth returns via the `mylouise://auth/callback` deep link straight to
 * Home — the onboarding stack never remounts and the patient's answers sat
 * in AsyncStorage forever (and could contaminate a later onboarding
 * re-entry).
 *
 * Mounted once the user is authenticated, this checks for saveable local
 * answers, waits for backend provisioning of the patient claim the same way
 * CreateAccountScreen does, and fires the shared save. The single-flight
 * guard in useSaveOnboardingData means whichever path starts first wins —
 * this never double-saves alongside CreateAccountScreen.
 */
export function OnboardingSaveBoot() {
  const stytch = useStytch();
  const { user } = useStytchUser();
  const saveToApi = useSaveOnboardingToApi();
  // One attempt per app session: a hard failure here shouldn't loop the
  // PATCH forever in the background. Storage is only cleared on success, so
  // the next cold boot retries naturally.
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!user || attemptedRef.current) return;
    let cancelled = false;

    const rescuePendingOnboarding = async () => {
      const stored = await loadOnboardingData();
      if (cancelled || !stored) return;
      if (!hasSaveableData(stored)) {
        // Nothing worth a PATCH — clear the leftovers so a future
        // onboarding re-entry starts clean.
        await clearOnboardingData();
        return;
      }

      attemptedRef.current = true;

      // Wait for the backend to provision tenant/patient trusted metadata,
      // mirroring CreateAccountScreen's post-auth wait.
      let hasMetadata = false;
      for (let i = 0; i < METADATA_MAX_RETRIES; i++) {
        const metadata = stytch.user.getSync()?.trusted_metadata ?? {};
        if (metadata["patient_id_in_dashboard_api"]) {
          hasMetadata = true;
          break;
        }
        try {
          await stytch.user.get();
        } catch {
          // ignore refresh errors, keep retrying
        }
        if (i < METADATA_MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, METADATA_RETRY_DELAY_MS));
        }
        if (cancelled) return;
      }

      if (cancelled || !hasMetadata) {
        if (!hasMetadata) {
          console.warn(
            "[OnboardingSaveBoot] Patient not provisioned within the retry window; onboarding answers kept locally for the next attempt.",
          );
        }
        return;
      }

      const stytchPhone = user.phone_numbers?.[0]?.phone_number;
      try {
        const saveKey = createOnboardingSaveKey(
          user.user_id,
          stored,
          stytchPhone,
        );
        const idempotencyKey = createOnboardingSaveIdempotencyKey(saveKey);
        await runOnboardingSaveOnce(saveKey, () =>
          saveToApi.save(stored, stytchPhone, idempotencyKey),
        );
        console.info(
          "[OnboardingSaveBoot] Rescued locally stored onboarding answers.",
        );
      } catch (err) {
        // Keep the data local; the next boot retries. Partner-invite accept
        // failures are included here — the invite stays in storage so the
        // user-facing flows can surface it.
        console.warn(
          "[OnboardingSaveBoot] Deferred onboarding save failed:",
          err,
        );
      }
    };

    rescuePendingOnboarding();
    return () => {
      cancelled = true;
    };
  }, [user, stytch, saveToApi]);

  return null;
}
